import { getPost, listPosts } from "./blog";
import { renderPostContentHtml } from "./blog-content-html";
import { blogPostCanonicalUrl, resolveBlogAssetUrl } from "./blog-seo";
import { getMessages } from "./i18n";
import { resolveLicense, SITE_DEFAULT_LICENSE, type LicenseInfo } from "./license";
import { localeByCode } from "./locale-registry";
import { absoluteLocalizedUrl, escapeXml } from "./seo";
import { locales, type Locale, type SiteContext } from "./site";

// Public, locale-aware feed endpoints. The base locale (en) is served unprefixed
// (/blog/feed.xml); every other locale gets its public prefix (/ua/blog/feed.xml,
// /pl/blog/feed.xml, …) via localizeUrl — mirroring the rest of the site's URLs.
export const FEED_PATHS = {
  rss: "/blog/feed.xml",
  atom: "/blog/atom.xml",
  json: "/blog/feed.json",
} as const;

export type FeedFormat = keyof typeof FEED_PATHS;

export const FEED_CONTENT_TYPES: Record<FeedFormat, string> = {
  rss: "application/rss+xml",
  atom: "application/atom+xml",
  json: "application/feed+json",
};

// Most readers cap how much history they keep; a few dozen entries is plenty and
// keeps the payload small. Posts are newest-first, so we slice the head.
const MAX_ITEMS = 50;

const AUTHOR_NAME = "inext";
const AUTHOR_URL = "https://inext.ltd";
const FEED_ICON = "https://inext.ltd/favicon.svg";

type FeedItem = {
  title: string;
  url: string;
  summary: string;
  contentHtml: string;
  publishedIso: string; // source date, "YYYY-MM-DD"
  tags: string[];
  image?: string;
  license: LicenseInfo;
};

type FeedMeta = {
  title: string;
  description: string;
  language: string; // BCP-47
  langCode: Locale;
  homeUrl: string;
  updatedIso: string; // "YYYY-MM-DD"
};

// Source dates are bare calendar days; pin them to UTC midnight so the same value
// renders deterministically regardless of the server's timezone.
function rfc822(dateIso: string): string {
  return new Date(`${dateIso}T00:00:00Z`).toUTCString();
}

function rfc3339(dateIso: string): string {
  return new Date(`${dateIso}T00:00:00Z`).toISOString();
}

// Wrap HTML in CDATA for RSS <content:encoded>. The only sequence that can break
// out of a CDATA section is "]]>", so split it across two sections.
function cdata(html: string): string {
  return `<![CDATA[${html.replace(/]]>/g, "]]]]><![CDATA[>")}]]>`;
}

function feedItems(site: SiteContext): FeedItem[] {
  return listPosts(site)
    .filter((post) => post.published)
    .slice(0, MAX_ITEMS)
    .map((summary) => {
      // listPosts() drops the body to keep summaries light; refetch the full post
      // for the content-bearing feeds. It always resolves here (same context).
      const full = getPost(site, summary.slug);
      const contentHtml = full
        ? renderPostContentHtml(full.content, site.locale, summary.slug)
        : "";
      return {
        title: summary.title,
        url: blogPostCanonicalUrl(summary),
        summary: summary.excerpt,
        contentHtml,
        publishedIso: summary.publishedAt,
        tags: summary.tags,
        image: summary.heroImage
          ? resolveBlogAssetUrl(summary.heroImage, summary.locale)
          : undefined,
        license: resolveLicense(summary.license),
      };
    });
}

function feedMeta(site: SiteContext, items: FeedItem[]): FeedMeta {
  const messages = getMessages(site.locale);
  return {
    title: messages.blogTitle,
    description: messages.blogDescription,
    language: localeByCode(site.locale).intl,
    langCode: site.locale,
    homeUrl: absoluteLocalizedUrl("/blog", site.locale),
    // Newest post drives the feed's "last updated"; fall back to a stable epoch
    // so an empty feed is still valid.
    updatedIso: items[0]?.publishedIso ?? "1970-01-01",
  };
}

// Cross-locale autodiscovery: every feed advertises the same format in the other
// locales, mirroring the site's hreflang strategy.
function localeAlternates(format: FeedFormat, current: Locale) {
  return locales
    .filter((code) => code !== current)
    .map((code) => ({ code, href: absoluteLocalizedUrl(FEED_PATHS[format], code) }));
}

export function buildRssFeed(site: SiteContext): string {
  const items = feedItems(site);
  const meta = feedMeta(site, items);
  const self = absoluteLocalizedUrl(FEED_PATHS.rss, site.locale);

  const itemXml = items
    .map((item) => {
      const lines = [
        "    <item>",
        `      <title>${escapeXml(item.title)}</title>`,
        `      <link>${escapeXml(item.url)}</link>`,
        `      <guid isPermaLink="true">${escapeXml(item.url)}</guid>`,
        `      <pubDate>${rfc822(item.publishedIso)}</pubDate>`,
        `      <dc:creator>${escapeXml(AUTHOR_NAME)}</dc:creator>`,
        `      <description>${escapeXml(item.summary)}</description>`,
        `      <content:encoded>${cdata(item.contentHtml)}</content:encoded>`,
        `      <dc:rights>${escapeXml(item.license.label)}</dc:rights>`,
        ...(item.license.url
          ? [`      <atom:link rel="license" href="${escapeXml(item.license.url)}" />`]
          : []),
        ...item.tags.map((tag) => `      <category>${escapeXml(tag)}</category>`),
      ];
      if (item.image) {
        lines.push(
          `      <media:content url="${escapeXml(item.image)}" medium="image" />`,
          `      <enclosure url="${escapeXml(item.image)}" type="image/jpeg" />`,
        );
      }
      lines.push("    </item>");
      return lines.join("\n");
    })
    .join("\n");

  const altLinks = localeAlternates("rss", site.locale)
    .map(
      ({ code, href }) =>
        `    <atom:link rel="alternate" hreflang="${code}" href="${escapeXml(href)}" type="application/rss+xml" />`,
    )
    .join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0" xmlns:atom="http://www.w3.org/2005/Atom" xmlns:content="http://purl.org/rss/1.0/modules/content/" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:media="http://search.yahoo.com/mrss/">
  <channel>
    <title>${escapeXml(meta.title)}</title>
    <link>${escapeXml(meta.homeUrl)}</link>
    <description>${escapeXml(meta.description)}</description>
    <language>${escapeXml(meta.language)}</language>
    <copyright>${escapeXml(resolveLicense(SITE_DEFAULT_LICENSE).label)}</copyright>
    <lastBuildDate>${rfc822(meta.updatedIso)}</lastBuildDate>
    <generator>inext.ltd</generator>
    <atom:link href="${escapeXml(self)}" rel="self" type="application/rss+xml" />
${altLinks ? `${altLinks}\n` : ""}    <image>
      <url>${escapeXml(FEED_ICON)}</url>
      <title>${escapeXml(meta.title)}</title>
      <link>${escapeXml(meta.homeUrl)}</link>
    </image>
${itemXml}
  </channel>
</rss>
`;
}

export function buildAtomFeed(site: SiteContext): string {
  const items = feedItems(site);
  const meta = feedMeta(site, items);
  const self = absoluteLocalizedUrl(FEED_PATHS.atom, site.locale);

  const entryXml = items
    .map((item) => {
      const categories = item.tags
        .map((tag) => `    <category term="${escapeXml(tag)}" />`)
        .join("\n");
      return [
        "  <entry>",
        `    <title>${escapeXml(item.title)}</title>`,
        `    <id>${escapeXml(item.url)}</id>`,
        `    <link rel="alternate" type="text/html" href="${escapeXml(item.url)}" />`,
        `    <published>${rfc3339(item.publishedIso)}</published>`,
        `    <updated>${rfc3339(item.publishedIso)}</updated>`,
        `    <summary type="text">${escapeXml(item.summary)}</summary>`,
        `    <content type="html">${escapeXml(item.contentHtml)}</content>`,
        `    <rights type="text">${escapeXml(item.license.label)}</rights>`,
        item.license.url
          ? `    <link rel="license" href="${escapeXml(item.license.url)}" />`
          : "",
        item.image ? `    <link rel="enclosure" type="image/jpeg" href="${escapeXml(item.image)}" />` : "",
        categories,
        "  </entry>",
      ]
        .filter(Boolean)
        .join("\n");
    })
    .join("\n");

  const altLinks = localeAlternates("atom", site.locale)
    .map(
      ({ code, href }) =>
        `  <link rel="alternate" hreflang="${code}" type="application/atom+xml" href="${escapeXml(href)}" />`,
    )
    .join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>
<feed xmlns="http://www.w3.org/2005/Atom" xml:lang="${meta.langCode}">
  <title>${escapeXml(meta.title)}</title>
  <subtitle>${escapeXml(meta.description)}</subtitle>
  <id>${escapeXml(meta.homeUrl)}</id>
  <link rel="self" type="application/atom+xml" href="${escapeXml(self)}" />
  <link rel="alternate" type="text/html" href="${escapeXml(meta.homeUrl)}" />
${altLinks ? `${altLinks}\n` : ""}  <updated>${rfc3339(meta.updatedIso)}</updated>
  <icon>${escapeXml(FEED_ICON)}</icon>
  <rights type="text">${escapeXml(resolveLicense(SITE_DEFAULT_LICENSE).label)}</rights>
  <generator uri="${escapeXml(AUTHOR_URL)}">inext.ltd</generator>
  <author>
    <name>${escapeXml(AUTHOR_NAME)}</name>
    <uri>${escapeXml(AUTHOR_URL)}</uri>
  </author>
${entryXml}
</feed>
`;
}

export function buildJsonFeed(site: SiteContext): string {
  const items = feedItems(site);
  const meta = feedMeta(site, items);
  const self = absoluteLocalizedUrl(FEED_PATHS.json, site.locale);

  const feed = {
    version: "https://jsonfeed.org/version/1.1",
    title: meta.title,
    home_page_url: meta.homeUrl,
    feed_url: self,
    description: meta.description,
    language: meta.language,
    icon: FEED_ICON,
    favicon: FEED_ICON,
    authors: [{ name: AUTHOR_NAME, url: AUTHOR_URL }],
    items: items.map((item) => ({
      id: item.url,
      url: item.url,
      title: item.title,
      summary: item.summary,
      content_html: item.contentHtml,
      date_published: rfc3339(item.publishedIso),
      language: meta.language,
      authors: [{ name: AUTHOR_NAME, url: AUTHOR_URL }],
      ...(item.tags.length ? { tags: item.tags } : {}),
      ...(item.image ? { image: item.image } : {}),
      // JSON Feed has no standard license field; expose it as a custom extension
      // (spec reserves keys starting with "_" for this).
      _license: {
        name: item.license.label,
        republishable: item.license.republishable,
        ...(item.license.url ? { url: item.license.url } : {}),
      },
    })),
  };

  return `${JSON.stringify(feed, null, 2)}\n`;
}

// <head> autodiscovery links for the three formats, scoped to the active locale.
// Feed readers and browser extensions look for these on blog pages.
export function feedDiscoveryLinks(
  locale: Locale,
): Array<{ rel: string; type: string; title: string; href: string }> {
  const messages = getMessages(locale);
  return [
    {
      rel: "alternate",
      type: FEED_CONTENT_TYPES.rss,
      title: `${messages.blogTitle} (RSS)`,
      href: absoluteLocalizedUrl(FEED_PATHS.rss, locale),
    },
    {
      rel: "alternate",
      type: FEED_CONTENT_TYPES.atom,
      title: `${messages.blogTitle} (Atom)`,
      href: absoluteLocalizedUrl(FEED_PATHS.atom, locale),
    },
    {
      rel: "alternate",
      type: FEED_CONTENT_TYPES.json,
      title: `${messages.blogTitle} (JSON Feed)`,
      href: absoluteLocalizedUrl(FEED_PATHS.json, locale),
    },
  ];
}

export const FEED_BUILDERS: Record<FeedFormat, (site: SiteContext) => string> = {
  rss: buildRssFeed,
  atom: buildAtomFeed,
  json: buildJsonFeed,
};
